import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, ShieldAlert, ClipboardList, Paperclip, X, ImageIcon, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { resizeImageToDataURL } from "@/lib/avatar";
import { toast } from "sonner";

const STATUSES = ["open", "in_progress", "under_review", "resolved"];
const STATUS_LABEL = { open: "Open", in_progress: "In Progress", under_review: "Under Review", resolved: "Resolved" };
const SEV = {
  critical: "bg-[#ff3b5c20] text-[#ff3b5c] border-[#ff3b5c40]",
  high: "bg-[#ff9f1c20] text-[#ff9f1c] border-[#ff9f1c40]",
  medium: "bg-[#ffc70020] text-[#ffc700] border-[#ffc70040]",
  low: "bg-[#ff000022] text-[color:var(--accent)] border-[#ff000044]",
};

export default function Incidents() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const canManage = user.role === "admin" || user.role === "manager";

  const load = () => api.get("/incidents").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    api.get("/incident-templates").then(({ data }) => setTemplates(data)).catch(() => setTemplates([]));
  }, []);

  const toggleSelect = (id) => setSelected((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelection = () => setSelected(new Set());

  const grouped = STATUSES.reduce((acc, s) => { acc[s] = items.filter((i) => i.status === s); return acc; }, {});
  const updateStatus = async (id, status) => { await api.patch(`/incidents/${id}`, { status }); load(); };

  const undoRestore = async (doc) => {
    try {
      await api.post("/incidents/restore", { doc });
      toast.success("Incident restored");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };
  const removeIncident = async (id) => {
    try {
      const { data } = await api.delete(`/incidents/${id}`);
      load();
      toast(`Incident deleted`, {
        duration: 6000,
        action: { label: "Undo", onClick: () => undoRestore(data.deleted) },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} incident${ids.length === 1 ? "" : "s"}? Undo available for 6s.`)) return;
    try {
      const { data } = await api.post("/incidents/bulk-delete", { ids });
      clearSelection();
      load();
      toast(`Deleted ${data.count} incident${data.count === 1 ? "" : "s"}`, {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            for (const doc of data.deleted) await api.post("/incidents/restore", { doc }).catch(() => {});
            toast.success(`Restored ${data.deleted.length}`);
            load();
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="incidents-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#ff3b5c]">Workplace Safety</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Incident Reports</h1>
          <p className="text-sm text-slate-400 mt-1">{templates.filter((t) => t.enabled).length} report type(s) available</p>
        </div>
        <div className="flex gap-2 items-center">
          {canManage && selected.size > 0 && (
            <button
              onClick={bulkDelete}
              data-testid="inc-bulk-delete"
              className="px-3 py-2 bg-[#ff3b5c] text-white rounded-lg font-bold text-sm inline-flex items-center gap-2 hover:opacity-90"
            >
              <Trash2 size={14} /> Delete {selected.size} selected
            </button>
          )}
          {user.role === "admin" && (
            <Link to="/incident-templates" data-testid="nav-incident-templates" className="px-4 py-2.5 border border-slate-700 hover:border-[color:var(--accent)] rounded-lg font-bold text-sm text-slate-200 inline-flex items-center gap-2">
              <ClipboardList size={14} /> Templates
            </Link>
          )}
          <button onClick={() => setOpen(true)} data-testid="new-incident" className="px-4 py-2.5 bg-[#ff3b5c] text-white rounded-lg font-bold flex items-center gap-2 hover:opacity-90">
            <Plus size={16} /> Report Incident
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {STATUSES.map((s) => (
          <div key={s} className="card-surface p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-slate-400">{STATUS_LABEL[s]}</div>
              <span className="text-xs font-bold text-slate-300">{grouped[s].length}</span>
            </div>
            <div className="space-y-2">
              {grouped[s].map((i) => (
                <div key={i.id} data-testid={`inc-${i.id}`} className={`bg-[#0b0d12] border rounded-lg p-3 hover:border-[#ff000044] transition-colors ${selected.has(i.id) ? "border-[color:var(--accent)]" : "border-[#2a3447]"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      {canManage && (
                        <input
                          type="checkbox"
                          checked={selected.has(i.id)}
                          onChange={() => toggleSelect(i.id)}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`inc-check-${i.id}`}
                          aria-label={`Select ${i.title}`}
                          className="mt-1 shrink-0 accent-[color:var(--accent)] cursor-pointer"
                        />
                      )}
                      <div className="font-semibold text-sm truncate">{i.title}</div>
                    </div>
                    <span className={`pill border shrink-0 ${SEV[i.severity]}`}>{i.severity}</span>
                  </div>
                  {i.template_name && <div className="text-[10px] uppercase tracking-widest text-[color:var(--accent)] mt-1">{i.template_name}</div>}
                  {i.description && <div className="text-xs text-slate-400 mt-1 line-clamp-2">{i.description}</div>}
                  {i.values && Object.keys(i.values).length > 0 && (
                    <details className="mt-2">
                      <summary className="text-[10px] uppercase tracking-widest text-slate-500 cursor-pointer hover:text-slate-300">Details ({Object.keys(i.values).length})</summary>
                      <div className="mt-2 space-y-1">
                        {Object.entries(i.values).map(([k, v]) => (
                          <div key={k} className="text-xs">
                            <div className="text-slate-500 uppercase tracking-wider text-[10px]">{k}</div>
                            <div className="text-slate-300">{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v || "—")}</div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                  {i.attachments && i.attachments.length > 0 && (
                    <div className="mt-2 flex gap-1 flex-wrap" data-testid={`inc-attachments-${i.id}`}>
                      {i.attachments.map((src, k) => (
                        <a key={`${i.id}-att-${src}`} href={src} target="_blank" rel="noopener noreferrer" title="Open full size">
                          <img src={src} alt={`Attachment ${k + 1}`} loading="lazy" className="w-14 h-14 rounded object-cover border border-slate-700 hover:border-[color:var(--accent)]" />
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2 flex items-center justify-between gap-2">
                    <span className="truncate">{i.reporter_name}</span>
                    {canManage && (
                      <button
                        onClick={() => removeIncident(i.id)}
                        data-testid={`inc-delete-${i.id}`}
                        title="Delete incident"
                        className="text-slate-500 hover:text-[#ff3b5c] shrink-0"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                  {canManage && (
                    <select value={i.status} onChange={(e) => updateStatus(i.id, e.target.value)} data-testid={`inc-status-${i.id}`} className="mt-2 w-full bg-[#121620] border border-[#2a3447] rounded text-xs px-2 py-1">
                      {STATUSES.map((ss) => <option key={ss} value={ss}>{STATUS_LABEL[ss]}</option>)}
                    </select>
                  )}
                </div>
              ))}
              {grouped[s].length === 0 && (
                <div className="text-xs text-slate-600 text-center py-6">
                  <ShieldAlert size={20} className="mx-auto mb-2" /> None
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {open && <CreateModal templates={templates.filter((t) => t.enabled)} onClose={() => setOpen(false)} onCreated={() => { load(); setOpen(false); }} />}
    </div>
  );
}

function CreateModal({ templates, onClose, onCreated }) {
  const [templateId, setTemplateId] = useState(templates[0]?.id || "");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [values, setValues] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const template = templates.find((t) => t.id === templateId);

  const setVal = (fid, v) => setValues((s) => ({ ...s, [fid]: v }));

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    const remaining = 5 - attachments.length;
    const picks = Array.from(files).slice(0, remaining);
    try {
      const dataUrls = await Promise.all(picks.map((f) => resizeImageToDataURL(f, { maxDim: 1200, quality: 0.75 })));
      setAttachments((a) => [...a, ...dataUrls]);
    } catch (e) {
      console.error("Attachment upload failed:", e);
      toast.error("One or more images could not be attached");
    }
  };
  const removeAttachment = (idx) => setAttachments((a) => a.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/incidents", { title, description, severity, template_id: templateId || null, values, attachments });
      onCreated();
    } catch (err) {
      console.error("Incident submission failed:", err);
      toast.error(err.response?.data?.detail || "Failed to submit report");
    } finally {
      setBusy(false);
    }
  };
  const inputCls = "w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)] text-sm";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} className="card-surface p-6 w-full max-w-lg max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="inc-modal">
        <h2 className="text-xl font-bold mb-4">Report an Incident</h2>
        {templates.length > 0 && (
          <label className="block mb-3">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Report type</div>
            <select value={templateId} onChange={(e) => { setTemplateId(e.target.value); setValues({}); }} data-testid="inc-template" className={inputCls}>
              <option value="">Generic incident</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {template?.description && <div className="text-xs text-slate-500 mt-1">{template.description}</div>}
          </label>
        )}

        <label className="block mb-3">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Title *</div>
          <input value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="inc-title" className={inputCls} />
        </label>
        <label className="block mb-3">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Summary</div>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} data-testid="inc-desc" className={inputCls + " resize-none"} />
        </label>

        {template && template.fields?.length > 0 && (
          <div className="space-y-3 mb-3 p-3 rounded-lg border border-slate-800">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--accent)]">{template.name} · fields</div>
            {template.fields.map((f) => (
              <label key={f.id} className="block">
                <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">
                  {f.label} {f.required && <span className="text-[#ff3b5c]">*</span>}
                </div>
                {f.type === "textarea" ? (
                  <textarea rows={3} value={values[f.id] || ""} onChange={(e) => setVal(f.id, e.target.value)} required={f.required} className={inputCls + " resize-none"} data-testid={`inc-field-${f.id}`} />
                ) : f.type === "select" ? (
                  <select value={values[f.id] || ""} onChange={(e) => setVal(f.id, e.target.value)} required={f.required} className={inputCls} data-testid={`inc-field-${f.id}`}>
                    <option value="">— pick one —</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === "checkbox" ? (
                  <label className="inline-flex items-center gap-2 text-sm text-slate-300">
                    <input type="checkbox" checked={!!values[f.id]} onChange={(e) => setVal(f.id, e.target.checked)} className="accent-[color:var(--accent)] w-4 h-4" data-testid={`inc-field-${f.id}`} /> Yes
                  </label>
                ) : (
                  <input type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"} value={values[f.id] || ""} onChange={(e) => setVal(f.id, e.target.value)} required={f.required} className={inputCls} data-testid={`inc-field-${f.id}`} />
                )}
                {f.help && <div className="text-[11px] text-slate-500 mt-1">{f.help}</div>}
              </label>
            ))}
          </div>
        )}

        {/* ATTACHMENTS */}
        <div className="block mb-3">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1.5">
            <Paperclip size={12} /> Screenshots ({attachments.length}/5)
          </div>
          <label className="block cursor-pointer border border-dashed border-slate-700 hover:border-[color:var(--accent)] rounded-lg py-4 text-center text-slate-400 text-sm transition-colors" data-testid="inc-attach-label">
            <input type="file" accept="image/*" multiple onChange={(e) => handleFiles(e.target.files)} disabled={attachments.length >= 5} className="hidden" data-testid="inc-attach-input" />
            <ImageIcon size={16} className="inline mr-1.5" /> Click to add screenshots (up to 5)
          </label>
          {attachments.length > 0 && (
            <div className="mt-2 flex gap-2 flex-wrap" data-testid="inc-attach-previews">
              {attachments.map((src, idx) => (
                <div key={`att-${src.slice(0, 40)}-${idx}`} className="relative">
                  <img src={src} alt={`Attachment ${idx + 1}`} className="w-20 h-20 object-cover rounded-lg border border-slate-700" />
                  <button type="button" onClick={() => removeAttachment(idx)} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#ff3b5c] text-white grid place-items-center" title="Remove" data-testid={`inc-attach-del-${idx}`}>
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <label className="block mb-3">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Severity</div>
          <select value={severity} onChange={(e) => setSeverity(e.target.value)} data-testid="inc-sev" className={inputCls}>
            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
          </select>
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200">Cancel</button>
          <button type="submit" disabled={busy} data-testid="inc-submit" className="px-4 py-2 rounded-lg bg-[#ff3b5c] text-white font-bold text-sm disabled:opacity-60">
            {busy ? "Submitting…" : "Submit Report"}
          </button>
        </div>
      </form>
    </div>
  );
}
